import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import OptimizedImage from "@/components/OptimizedImage";
import { Star, CheckCircle, XCircle, ExternalLink } from "lucide-react";

interface Product {
  id: string;
  name: string;
  price: string;
  rating: number;
  reviewCount: number;
  image: string;
  pros: string[];
  cons: string[];
  affiliateLink: string;
  description: string;
}

interface ProductCardProps {
  product: Product;
  rank?: number;
}

export const ProductCard = ({ product, rank }: ProductCardProps) => {
  const handleBuyClick = () => {
    window.open(product.affiliateLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative overflow-hidden">
      {rank && (
        <div className="absolute top-4 left-4 z-10">
          <Badge variant="default" className="bg-primary text-primary-foreground font-bold text-lg px-3 py-1">
            #{rank}
          </Badge>
        </div>
      )}
      
      <CardHeader className="relative">
        <div className="bg-muted rounded-lg mb-4 overflow-hidden">
          <OptimizedImage 
            src={product.image} 
            alt={product.name}
            className="w-full h-auto max-h-64 object-contain group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        
        <div className="space-y-2">
          <CardTitle className="text-xl group-hover:text-primary transition-colors">
            {product.name}
          </CardTitle>
          <div className="text-muted-foreground text-sm space-y-2">
            {product.description.includes('Specifications:') ? (
              <div className="space-y-3">
                {product.description.split('\n').map((line, index) => {
                  const trimmedLine = line.trim();
                  if (!trimmedLine) return null;
                  
                  if (trimmedLine.startsWith('Specifications:')) {
                    return <h4 key={index} className="font-semibold text-foreground">{trimmedLine}</h4>;
                  }
                  
                  if (trimmedLine.startsWith('Reviews:')) {
                    return <h4 key={index} className="font-semibold text-foreground mt-4">{trimmedLine}</h4>;
                  }
                  
                  if (trimmedLine.startsWith('    ')) {
                    // Sub-bullet points (indented)
                    return (
                      <div key={index} className="ml-4">
                        <span className="text-xs">• {trimmedLine.replace(/^    /, '')}</span>
                      </div>
                    );
                  }
                  
                  if (trimmedLine.includes(':')) {
                    // Main bullet points
                    return (
                      <div key={index} className="flex items-start gap-2">
                        <span className="text-xs mt-1">•</span>
                        <span>{trimmedLine}</span>
                      </div>
                    );
                  }
                  
                  // Regular text
                  return <p key={index}>{trimmedLine}</p>;
                })}
              </div>
            ) : (
              <p>{product.description}</p>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Price and Rating */}
        <div className="flex items-center justify-between">
          <div className="text-2xl font-bold text-primary">
            {product.price}
          </div>
          <div className="flex items-center gap-1">
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            <span className="font-semibold">{product.rating}</span>
            <span className="text-muted-foreground text-sm">
              Expert Review
            </span>
          </div>
        </div>

        {/* Pros and Cons */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <h4 className="font-semibold text-green-600 mb-2 flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              Pros
            </h4>
            <ul className="space-y-1">
              {product.pros.slice(0, 3).map((pro, index) => (
                <li key={index} className="text-muted-foreground text-xs">
                  • {pro}
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-red-600 mb-2 flex items-center gap-1">
              <XCircle className="h-4 w-4" />
              Cons
            </h4>
            <ul className="space-y-1">
              {product.cons.slice(0, 3).map((con, index) => (
                <li key={index} className="text-muted-foreground text-xs">
                  • {con}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Buy Button */}
        <Button 
          variant="amazon" 
          className="w-full" 
          onClick={handleBuyClick}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
                      Buy Now
        </Button>
      </CardContent>
    </Card>
  );
};